<template>
  <div v-if="results?.comparisonResults?.length" class="wrapper mask-wave-t-b my-12">
    <div class="bg-white pt-28 pb-8 px-box-4 lg:px-16">
      <SliceTitle class="max-w-3xl mx-auto title lg:text-center" :value="content.title" />
      <div class="text-center mt-6 mb-12 lg:mt-8">
        <span :class="{ 'chromatic-ignore': isChromatic }" class="text-gray-400">
          {{ $t('last_updated', { ago: '' }) }}
          <NuxtTime :datetime="results.timestamp" :locale="$lang" relative />
          &middot;
        </span>
        <a class="link" href="#imt-search-modal">
          {{ $t('providers_exchange_rates.see_live_results') }}
        </a>
      </div>

      <div class="flip-x-180 scroller relative" :class="{ 'mb-6': isOpen }">
        <table class="flip-x-180 w-full max-w-6xl mx-auto">
          <thead>
            <tr class="bg-gray-50 text-gray-500">
              <th class="font-bold text-18 lg-text-center">
                {{
                  $t('providers_exchange_rates.send_x_with', {
                    amount: formatMoney(amount, content.corridor.currencyFrom.iso, $lang, 0),
                  })
                }}
              </th>
              <th class="font-bold text-18 lg-text-center">
                {{ $t('providers_exchange_rates.your_recipient_gets') }}
              </th>
              <th class="font-normal">
                {{ $t('exchange_rate') }}
              </th>
              <th class="font-normal">
                {{ $t('providers_exchange_rates.fees') }}
              </th>
            </tr>
          </thead>

          <tbody class="relative" :class="{ gradient: !isOpen }">
            <tr
              v-for="(result, i) in results.comparisonResults"
              :key="result.pspId"
              :class="{ hidden: !isOpen && i > MAX_DISPLAYED_RESULTS }"
            >
              <td class="px-6 py-8 relative">
                <MImage
                  class="object-contain h-8 lg:h-11 lg:mx-auto provider-logo"
                  :src="result.pspLogo"
                  :alt="result.pspLabel"
                  :lazy="false"
                />
                <span class="absolute top-0 bottom-0 left-0 right-0 px-6 py-8">
                  <a
                    :href="addAffiliateMobileAppParams(result.affiliateUrl)"
                    class="link block h-8 lg:h-11 lg:mx-auto"
                    target="_blank"
                    rel="nofollow noopener"
                  />
                </span>
              </td>
              <td class="font-bold text-center px-8 text-18 lg:text-24">
                {{ formatMoney(result.amountReceived, content.corridor.currencyTo.iso, $lang, 2) }}
                <a class="text-center text-12 block link" href="#imt-search-modal">
                  {{ $t('providers_exchange_rates.chart_rates') }}
                </a>
              </td>
              <td class="text-18 px-8">
                <span>
                  {{ result.pspLabel }} {{ content.corridor.currencyFrom.iso }} {{ $t('to') }}
                  {{ content.corridor.currencyTo.iso }} {{ $t('rate') }}: <br />
                  1 {{ content.corridor.currencyFrom.iso }} =
                  {{ formatRate(result.exchangeRate, $lang) }}
                  {{ content.corridor.currencyTo.iso }}
                </span>
              </td>
              <td class="text-18 px-8">
                {{ formatMoney(result.fees, content.corridor.currencyFrom.iso, $lang, 0) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="text-center">
        <a class="btn btn-green pl-14 pr-14 py-5" href="#imt-search-modal">
          {{ $t('providers_exchange_rates.go_to_full_comparison') }}
        </a>
      </div>

      <div class="mt-10 mb-18 mx-2 text-center font-medium text-18 justify-items-center">
        <div class="max-w-2xl mx-auto">
          {{ $t('providers_exchange_rates.table_summary') }}
        </div>
      </div>

      <div v-if="!isOpen && hiddenResults > 0" class="my-10 mx-2 text-center">
        <button class="link" @click="isOpen = true">
          {{ $t('providers_exchange_rates.more_results', { count: hiddenResults }) }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { GQLProvidersExchangeRatesSlice } from './generated/providers-exchange-rates'
import type {
  GQLProvidersExchangeRatesComparison,
  GQLProvidersExchangeRatesComparisonVariables,
} from './generated/provider-exchange-rates-comparison'
import MUTATION from './provider-exchange-rates-comparison.graphql'

const MAX_DISPLAYED_RESULTS = 4
const EIGHT_WEEKS_IN_SECONDS = 4838400

const props = defineProps<{ content: GQLProvidersExchangeRatesSlice }>()

const isOpen = ref(false)

const hiddenResults = computed(() => {
  return results.value?.comparisonResults
    ? results.value.comparisonResults.length - MAX_DISPLAYED_RESULTS
    : 0
})

const amount = computed(() => {
  return props.content.corridor.defaultAmount || 1000
})

const { $apollo, $lang } = useNuxtApp()
const logger = useLogger('providers-exchange-rates')

function getPromo<Promo>(quote: { promos: Promo[] }) {
  return quote.promos.length ? quote.promos[quote.promos.length - 1] : null
}

const { data: results } = await useAsyncData('provider-exchange-rates', async () => {
  try {
    const { data, errors } = await $apollo.mutate<
      GQLProvidersExchangeRatesComparison,
      GQLProvidersExchangeRatesComparisonVariables
    >({
      mutation: MUTATION,
      variables: {
        lang: $lang,
        from: props.content.corridor.countryFrom.iso,
        to: props.content.corridor.countryTo.iso,
        currencyFrom: props.content.corridor.currencyFrom.iso,
        currencyTo: props.content.corridor.currencyTo.iso,
        amount: amount.value,
        maxAge: EIGHT_WEEKS_IN_SECONDS,
      },
    })
    if (errors || !data) {
      logger.error({ errors }, 'Error while loading provider exchange rates')
      return { comparisonResults: null, timestamp: null }
    }

    const timestamp = new Date(data.results.start).getTime()

    const comparisonResults = data.results.providerQuotes
      .flatMap((pq) => {
        pq.quotes.sort((a, b) => {
          const aPromo = getPromo(a)
          const bPromo = getPromo(b)
          const aAmount = aPromo?.receivedAmount ?? a.receivedAmount
          const bAmount = bPromo?.receivedAmount ?? b.receivedAmount
          return bAmount - aAmount
        })
        const quote = pq.quotes[0]
        const promo = getPromo(quote)
        return {
          pspId: pq.psp.slug,
          affiliateUrl: pq.psp.affiliateUrl,
          pspLogo: pq.psp.logo.sm,
          pspLabel: pq.psp.displayName,
          amountReceived: promo?.receivedAmount ?? quote.receivedAmount,
          exchangeRate: promo?.rate ?? quote.rate,
          fees: promo?.fee?.total ?? quote.fee.total,
        }
      })
      .sort((a, b) => b.amountReceived - a.amountReceived)

    return { comparisonResults, timestamp }
  } catch (error) {
    logger.error({ error }, 'Failed to load provider exchange rates')
  }
  return { comparisonResults: null, timestamp: null }
})
</script>

<style scoped>
.gradient::after {
  @apply absolute h-2/5 bottom-0 -left-10 -right-10 z-0 bg-gradient-to-t from-white to-transparent pointer-events-none;
  content: '';
}

.provider-logo {
  max-width: unset;
}

th {
  @apply border border-gray-300 py-2 px-6 text-left lg:py-5 lg:px-8;
}

td {
  @apply whitespace-nowrap text-gray-700 border border-gray-300 py-7 lg:py-8;
}

/* hack to move the horizontal scrollbar on top */
.flip-x-180 {
  transform: rotateX(180deg);
}

.lg-text-center {
  @apply lg:text-center;
}

.scroller {
  @apply overflow-x-auto;
  /* For Firefox */
  scrollbar-width: thin;
  scrollbar-color: rgba(212, 212, 212) rgb(248, 248, 248);
}

.scroller::-webkit-scrollbar {
  @apply w-2 h-2 lg:hidden;
}

.scroller::-webkit-scrollbar-corner {
  @apply bg-transparent;
}

.scroller::-webkit-scrollbar-thumb {
  @apply bg-gray-300 rounded-10;
}

.scroller::-webkit-scrollbar-thumb:hover {
  @apply bg-gray-400;
}

.scroller::-webkit-scrollbar-track {
  @apply bg-gray-50 bg-clip-content;
}
</style>
